import { useCallback, useMemo, useState } from 'react';

import {
  ColumnFiltersState,
  FilterFn,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { FeedbackGetResponseDto } from '../../../../../../../backend/src/feedback/dto/get-feedback.response.dto';
import { FEEDBACK_QUESTIONS } from '../../../../../../../backend/src/feedback/feedback-questions';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { useGetOrganisationFeedback } from '@/api/queries/useGetOrganisationFeedback';
import { useGlobalAdminCheck } from '@/hooks/useGlobalAdminCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { FilterMultiSelect } from '@/new/components/filter/filter-multi-select';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { arrInArr, formatDatetimeForTable, includedInArr } from '@/new/lib/table-utils';

const fallbackTableData: FeedbackGetResponseDto[] = [];
const columnHelper = createColumnHelper<FeedbackGetResponseDto>();

function formatQuestion(questionId: string) {
  return FEEDBACK_QUESTIONS[questionId as keyof typeof FEEDBACK_QUESTIONS]?.question ?? questionId;
}

// Metadata is a free-form jsonb field; flatten values into strings so they can be used as
// discrete filter options. Nested objects are stringified in one go.
function normaliseMetadataValue(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => normaliseMetadataValue(item));
  }
  if (typeof value === 'object') {
    return [JSON.stringify(value)];
  }
  return [String(value)];
}

// Metadata keys and values vary per feedback submission, so filter options are derived from
// whatever is present in the current dataset rather than from a fixed schema.
function getMetadataFieldOptions(feedbackItems: FeedbackGetResponseDto[]) {
  const fieldOptions = new Map<string, Set<string>>();

  for (const item of feedbackItems) {
    if (!item.metadata) continue;

    for (const [key, rawValue] of Object.entries(item.metadata)) {
      const values = normaliseMetadataValue(rawValue);
      if (values.length === 0) continue;

      const existingValues = fieldOptions.get(key) ?? new Set<string>();
      values.forEach((value) => existingValues.add(value));
      fieldOptions.set(key, existingValues);
    }
  }

  return Array.from(fieldOptions.entries())
    .map(([key, values]) => ({
      key,
      options: Array.from(values).sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

function formatMetadata(metadata: FeedbackGetResponseDto['metadata']) {
  return JSON.stringify(metadata, null, 2);
}

// Stringifies metadata so its contents are reachable via the search box — otherwise metadata is
// only filterable through the per-key dropdowns.
const feedbackGlobalFilter: FilterFn<FeedbackGetResponseDto> = (row, _columnId, filterValue) => {
  const query = String(filterValue ?? '')
    .trim()
    .toLowerCase();

  if (!query) return true;

  const { createdBy, questionId, response, freeText, createdAt, metadata } = row.original;

  return [
    createdBy.email,
    formatQuestion(questionId),
    response,
    freeText,
    formatDatetimeForTable(createdAt),
    metadata ? formatMetadata(metadata) : '',
  ].some((value) => value?.toLowerCase().includes(query));
};

export function OrganisationFeedback() {
  const { organisationId } = useParams();
  if (!organisationId) return null;

  useGlobalAdminCheck();

  const { data: organisation } = useGetOrganisation(organisationId);
  const { data: feedbackData, isLoading } = useGetOrganisationFeedback(organisationId);

  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const metadataFieldOptions = useMemo(
    () => getMetadataFieldOptions(feedbackData ?? fallbackTableData),
    [feedbackData],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdBy.email', {
        id: 'createdBy.email',
        header: () => <span>User</span>,
        cell: (info) => <span>{info.getValue()}</span>,
        filterFn: includedInArr,
      }),
      columnHelper.accessor('questionId', {
        header: () => <span>Question</span>,
        cell: (info) => <span>{formatQuestion(info.getValue())}</span>,
        filterFn: includedInArr,
      }),
      columnHelper.accessor('response', {
        header: () => <span>Response</span>,
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor('freeText', {
        header: () => <span>Notes</span>,
        cell: (info) => (
          <span className="line-clamp-3 max-w-96 break-words whitespace-pre-wrap">{info.getValue() || '—'}</span>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: () => <span>Submitted</span>,
        cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
      }),
      columnHelper.accessor('metadata', {
        header: () => <span>Metadata</span>,
        cell: (info) => {
          const value = info.getValue();
          if (!value) return <span>—</span>;

          return (
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm">
                  <Icon className="lucide--eye" /> View
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={8} className="w-[32rem] max-w-[calc(100vw-2rem)] p-2">
                <pre className="max-h-96 overflow-auto rounded-md border bg-gray-50 p-3 text-xs leading-5 text-gray-800">
                  {formatMetadata(value)}
                </pre>
              </PopoverContent>
            </Popover>
          );
        },
      }),
      ...metadataFieldOptions.map(({ key }) =>
        columnHelper.accessor((row) => normaliseMetadataValue(row.metadata?.[key]), {
          id: `metadata.${key}`,
          header: () => null,
          cell: () => null,
          enableHiding: true,
          filterFn: arrInArr,
        }),
      ),
    ],
    [metadataFieldOptions],
  );

  const userOptions = useMemo(() => {
    const users = (feedbackData ?? fallbackTableData)
      .map((item) => item.createdBy.email)
      .filter((email): email is string => Boolean(email));

    return Array.from(new Set(users)).sort((a, b) => a.localeCompare(b));
  }, [feedbackData]);

  const questionOptions = useMemo(() => {
    const questionIds = (feedbackData ?? fallbackTableData)
      .map((item) => item.questionId)
      .filter((questionId): questionId is string => Boolean(questionId));

    return Array.from(new Set(questionIds)).sort((a, b) => formatQuestion(a).localeCompare(formatQuestion(b)));
  }, [feedbackData]);

  const columnVisibility = useMemo(
    () => Object.fromEntries(metadataFieldOptions.map(({ key }) => [`metadata.${key}`, false])),
    [metadataFieldOptions],
  );

  const table = useReactTable({
    data: feedbackData ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: feedbackGlobalFilter,
    initialState: {
      sorting: [
        {
          id: 'createdAt',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
      columnFilters,
      columnVisibility,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
  });

  const clearAllFilters = () => {
    setGlobalFilter('');
    setColumnFilters([]);
  };

  const handleExport = useCallback(() => {
    const rows = table.getFilteredRowModel().rows.map((row) => row.original);
    if (rows.length === 0) return;

    const metadataKeys = metadataFieldOptions.map(({ key }) => key);
    const headers = ['User', 'Question', 'Response', 'Notes', 'Submitted', ...metadataKeys];

    const data = [
      headers,
      ...rows.map((item) => [
        item.createdBy.email ?? '',
        formatQuestion(item.questionId),
        item.response,
        item.freeText ?? '',
        formatDatetimeForTable(item.createdAt),
        ...metadataKeys.map((key) => normaliseMetadataValue(item.metadata?.[key]).join(', ')),
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(data);
    worksheet['!cols'] = [
      { wch: 28 },
      { wch: 42 },
      { wch: 14 },
      { wch: 40 },
      { wch: 22 },
      ...metadataKeys.map(() => ({ wch: 22 })),
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Feedback');

    const slug = (organisation?.name ?? organisationId).replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `feedback-${slug}-${date}.xlsx`);
  }, [table, metadataFieldOptions, organisation?.name, organisationId]);

  const userFilterValue = (table.getColumn('createdBy.email')?.getFilterValue() as string[]) ?? [];
  const questionFilterValue = (table.getColumn('questionId')?.getFilterValue() as string[]) ?? [];

  return (
    <>
      <Breadcrumb
        items={
          organisation
            ? [
                { href: '/admin/organisations', label: 'Admin', active: false },
                {
                  href: `/admin/organisations/${organisation.id}`,
                  label: organisation.name,
                  active: false,
                },
                {
                  href: `/admin/organisations/${organisation.id}/feedback`,
                  label: 'Feedback',
                  active: true,
                },
              ]
            : [{ href: '/admin/organisations', label: 'Admin', active: false }]
        }
      />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Feedback" />
        </PageHeader>
      </PageContainer>

      <ScrollableTable
        table={table}
        plural="feedback entries"
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        additionalButtons={
          <Button onClick={handleExport} disabled={table.getFilteredRowModel().rows.length === 0}>
            <Icon className="lucide--download" />
            <span>Export</span>
          </Button>
        }
        filters={{
          popover: (
            <FilterPopoverContent clearAllFilters={clearAllFilters}>
              <div>
                <p className="mb-3 text-gray-600">User</p>
                <FilterMultiSelect
                  values={userFilterValue}
                  options={userOptions}
                  column={table.getColumn('createdBy.email')!}
                  placeholder="Select users..."
                />
              </div>
              <div>
                <p className="mb-3 text-gray-600">Question</p>
                <FilterMultiSelect
                  values={questionFilterValue}
                  options={questionOptions}
                  column={table.getColumn('questionId')!}
                  placeholder="Select questions..."
                  formatLabel={formatQuestion}
                />
              </div>
              {metadataFieldOptions.map(({ key, options }) => {
                const column = table.getColumn(`metadata.${key}`);
                if (!column || options.length === 0) return null;

                const selectedValues = (column.getFilterValue() as string[]) ?? [];

                return (
                  <div key={key}>
                    <p className="mb-3 text-gray-600">{key}</p>
                    <FilterMultiSelect
                      values={selectedValues}
                      options={options}
                      column={column}
                      placeholder={`Select ${key}...`}
                    />
                  </div>
                );
              })}
            </FilterPopoverContent>
          ),
          count: columnFilters.length,
          clearAll: clearAllFilters,
        }}
        loading={isLoading}
      />
    </>
  );
}
